import HooksFetcher from 'data/HooksFetcher';
import type { Bundle, BundleEntry, Flag } from 'fhir/r4';
import moment from 'moment';
import { forwardRef, useState, useImperativeHandle } from 'react';
import React from 'react';
import {
  Header,
  Message,
  Table,
  Image,
  Checkbox,
  Dimmer,
  Loader,
  Label,
  Button,
} from 'semantic-ui-react';
import useSWR from 'swr';
import type { Key } from 'swr';
import type { SWRConfiguration } from 'swr';
import type { Fetched, FlatAlert, FlatAlertPatient } from 'types';


import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher, { MultiFetcher } from '../../../data/FHIRFetcher';
import FHIRUpdater from '../../../data/FHIRUpdater';
import type { ApiFetchError } from '../../../services/api-client';
import * as FHIRUtils from '../../../utils/FHIRUtils';
import { mapKey } from '../../../utils/mapKey';

/**
 * One entry of the id-keyed patient map the alerts table renders names from: the raw
 * FHIR Patient alongside the display name parsed off it.
 */
type AlertPatientEntry = {
  resource: fhir4.Patient;
  name: string;
};

type AlertEntry = BundleEntry & { resource: Flag };

export type AlertsHandle = {
  refreshAlerts: () => void;
};

type AlertsProps = {
  onPatientSelected?: (selectedPatient?: unknown, taskId?: string, patientId?: string) => void;
  setActiveAlertsCount: (count: number) => void;
};

const Alerts = forwardRef<AlertsHandle, AlertsProps>((props, ref) => {
  const authInfo = useAuthContext();
  const [dimmerProcess, setDimmerProcess] = useState(false);
  const [alertsData, setAlertsData] = useState<FlatAlert[]>([]);
  const [patients, setPatients] = useState<Record<string, AlertPatientEntry> | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [alertsPayload, setAlertsPayload] = useState<Record<string, AlertEntry | undefined>>({});
  const [selectedAlerts, setSelectedAlerts] = useState<FlatAlert[]>([]);
  const [_selectedPayer, setSelectedPayer] = useState('All');

  // Exposing refreshAlerts() to Parent ('Home' in this case). No need to refresh the whole component..just refreshes the data.
  useImperativeHandle(ref, () => ({
    refreshAlerts() {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
      alertsMutate();
    },
  }));

  // setInterval(function () {getAlertsCount()}, 60000);

  // Annotated because SWR 0.5.7 defaults `Data` to `any` and a detached options
  // `const` infers nothing from its `useSWR` call site.
  const options: SWRConfiguration<Fetched<Bundle>> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data, _key, _config) => {
      //setAlertsData(data);
      setLoadingData(true);
      if (data && data.entry && data.entry.length) {
        const alertsList: FlatAlert[] = [];
        const patientsObj: FlatAlertPatient[] = [];
        // Handed back to `FHIRUpdater` when an alert is acknowledged. `resource` and
        // `id` are both `0..1`, so an entry missing either lands on one shared key.
        const payload: Record<string, AlertEntry | undefined> = {};
        data.entry.forEach((alert) => {
          // Asserted rather than narrowed with `bundleEntriesOf`, which would SKIP a
          // malformed entry — silently dropping an alert from the care manager's list,
          // where today the parse throws and the list fails loudly.
          alertsList.push(FHIRUtils.parseFHIRAlerts(alert.resource as Flag));
          patientsObj.push(FHIRUtils.parseAlertsPatients(alert.resource as Flag));
          payload[mapKey(alert.resource!.id)] = alert as AlertEntry;
        });
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. The date tiebreak does nothing:
         * alerts within one status come back in feed order, not newest-first.
         *
         * `FlatAlert` has no `date`, and `parseFHIRAlerts` never assigns one — it
         * writes `lastUpdated` and `period`, which is what the comment above calls
         * "last updated date". So `b.date - a.date` is `undefined - undefined`, i.e.
         * `NaN`, and a comparator returning `NaN` leaves the equal-status runs in
         * their original order.
         *
         * The status grouping still works, which is why this looks plausible. The
         * intended field is almost certainly `lastUpdated`, but that is a date STRING,
         * so `-` on it would be `NaN` too: fixing this needs a parse.
         */
        const sortedAlertsList = alertsList.sort(function (a, b) {
          if (a.status === b.status) {
            return (
              (b as FlatAlert & { date?: number }).date! -
              (a as FlatAlert & { date?: number }).date!
            );
          }
          return a.status > b.status ? 1 : -1;
        });
        setAlertsData(sortedAlertsList);
        setAlertsPayload(payload);

        // See the note on the traversal above for why this is asserted, not narrowed.
        const activeAlerts = data.entry.filter(
          (alert) => (alert.resource as Flag).status === 'active',
        );
        // Annotated because `{}` has no index signature: the accumulator is keyed
        // by a runtime org, and a group is only created holding its first member,
        // so an existing key always holds a non-empty array. The element type is
        // `FlatAlertPatient['id']` — optional, and pushed unfiltered.
        const orgPatients: Record<string, (string | undefined)[] | undefined> = {};

        patientsObj.forEach((patient) => {
          if (patient.org) {
            // `FlatAlertPatient.org` is `unknown` — it comes off `fhirpath.evaluate`
            // — and a truthiness test narrows that only to `{}`. `mapKey` names the
            // coercion the subscript already performed.
            const orgKey = mapKey(patient.org);
            // `hasOwnProperty`, matching upstream, rather than subscripting and testing the
            // result for truthiness. They disagree on any key naming an inherited
            // `Object.prototype` member: `orgPatients['constructor']` is the inherited function,
            // which is truthy, so the truthiness form takes the push arm and throws
            // `f.push is not a function`. Measured over 7 org keys, 5 diverge. `patient.org` is
            // an unconstrained server-supplied FHIR identifier (`parseAlertsPatients`), so this
            // is reachable rather than theoretical.
            //
            // `.call` rather than upstream's bare `orgPatients.hasOwnProperty(...)`: this repo
            // enables `no-prototype-builtins`, which rejects the bare form, so no spelling both
            // matches upstream's emit and passes lint. `.call` is the form the other 37 sites in
            // this app already use. See AUDIT.md §2.11.
            if (Object.prototype.hasOwnProperty.call(orgPatients, orgKey)) {
              orgPatients[orgKey]!.push(patient.id);
            } else {
              orgPatients[orgKey] = [patient.id];
            }
          }
        });

        const urls: string[] = [];
        if (Object.keys(orgPatients).length) {
          Object.keys(orgPatients).forEach((org) => {
            const patients = orgPatients[org]!;
            const uniquePatients = patients.filter((c, index) => {
              if (c && c !== '') return patients.indexOf(c) === index;
            });

            if (uniquePatients && uniquePatients.length) {
              urls.push(`/Patient/?organization=${org}&_id=${uniquePatients.join(',')}`);
            }
          });
          getPatients(urls);
        } else {
          setLoadingData(false);
        }
        props.setActiveAlertsCount(activeAlerts.length);
      }
    },
  };

  const getPatients = (urls: string[]) => {
    MultiFetcher<Bundle>(authInfo.token, ...urls).then(
      (patientsDataset) => {
        // Accumulates into an id-keyed map via the object spread below, so it is a record.
        let combinedPatients: Record<string, AlertPatientEntry> = {};
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. `MultiFetcher` resolves `undefined`,
         * not `[]`, when handed no URLs, so this `.forEach` throws on that arm.
         *
         * Reachable: the caller's gate is `Object.keys(orgPatients).length`, while
         * `urls` is only pushed to under the inner `uniquePatients.length` test. An
         * alert roster whose every patient id is absent or `''` passes the outer gate
         * and pushes nothing.
         *
         * Asserted rather than optional-chained: `!` erases, so the throw is preserved.
         */
        patientsDataset!.forEach((data) => {
          if (data && data.entry && data.entry.length) {
            const tempPatients: Record<string, AlertPatientEntry> = {};
            data.entry.forEach((item) => {
              // `resource` is `0..1`, asserted for the same reason as the `alert.resource`
              // reads above: narrowing would skip a malformed entry rather than throw.
              const name = FHIRUtils.parseFHIRPatientName(item.resource as fhir4.Patient);
              tempPatients[item.resource!.id!] = {
                resource: item.resource as fhir4.Patient,
                name: name,
              };
            });
            combinedPatients = { ...combinedPatients, ...tempPatients };
            setLoadingData(false);
          }
        });
        setPatients(combinedPatients);
      },
      (_error) => {},
    );
  };

  const { error: alertsError, mutate: alertsMutate } = useSWR<Fetched<Bundle>, ApiFetchError>(
    authInfo.practitionerId && [
      `/Flag/?identifier=Practitioner/${authInfo.practitionerId}&_sort=-date&_count=200`,
      authInfo.token,
    ],
    FHIRFetcher,
    options,
  );

  // Fetches active alerts counts every minute. setting refresh interval up to 60000 millisenonds (1 minute)  refreshInterval:60000
  const alertsCountOptions: SWRConfiguration<Fetched<number>> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    refreshInterval: 60000,
    onSuccess: (alertsCount, _key, _config) => {
      if (alertsCount && alertsCount > -1) {
        props.setActiveAlertsCount(alertsCount);
      }
    },
  };

  /*
   * SWR 0.5.7's `Key` admits `null` but not the `false | ''` this yields, though the
   * library treats any falsy key as "do not fetch". Rewriting to the `? […] : null`
   * form used elsewhere would edit the running expression.
   *
   * The `!== undefined` conjunct is redundant — the truthiness test precedes it.
   */
  useSWR<Fetched<number>>(
    (authInfo.practitionerId &&
      authInfo.practitionerId !== undefined && [
        `/hooks/alerts-count/${authInfo.practitionerId}`,
        authInfo.token,
      ]) as Key,
    HooksFetcher,
    alertsCountOptions,
  );

  const changeStatus = (alert: FlatAlert) => {
    const flagPayload = alertsPayload[alert.id]!.resource;

    if (flagPayload.status === 'active') {
      flagPayload.status = 'inactive';
    } else {
      flagPayload.status = 'active';
    }

    setDimmerProcess(true);

    FHIRUpdater('/Flag/' + flagPayload.id, authInfo.token, flagPayload).then(
      (_response) => {
        setDimmerProcess(false);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
        alertsMutate();
      },
      (_error) => {
        setDimmerProcess(false);
      },
    );

    setSelectedAlerts([]);
  };

  const handleFlagMulti = (alerts: FlatAlert[] | false, setAll: boolean) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    setAll &&
      alertsData.map((flag) => {
        if (flag.status === 'active') changeStatus(flag);
      });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    alerts &&
      alerts.forEach((flag) => {
        changeStatus(flag);
      });
  };

  const handleSelectFlag = (alert: FlatAlert) => {
    const temp = selectedAlerts;
    const index = temp.indexOf(alert);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    index === -1 ? temp.push(alert) : index === 0 ? temp.pop() : temp.splice(index, index);
    setSelectedAlerts(temp);
  };

  const openPatient = (flag: FlatAlert) => {
    const patientId = flag.patient as string;
    const taskId = flag.taskId as string | undefined;
    props.onPatientSelected!(patients![patientId]!.resource, taskId);
  };

  // Unreferenced, and `_selectedPayer` is never read either — the payer filter is wired
  // up at neither end. Typed from the body rather than a call site because there is no
  // call site to read one off. `textContent` is `string | null`; `setSelectedPayer`
  // takes `string`, so the `!` records a disagreement this dead path never exercises.
  function _handlePayerSelection(e: React.SyntheticEvent<HTMLElement>) {
    setSelectedPayer((e.target as HTMLElement).textContent);
  }
  return (
    <>
      {alertsData && alertsData.length && (
        <>
          <Dimmer active={dimmerProcess} inverted>
            <Loader />
          </Dimmer>
          <Button
            loading={loadingData}
            color="blue"
            onClick={() => handleFlagMulti(false, true)}
            floated="right"
            content="Mark all as read"
            icon="envelope open outline"
            labelPosition="right"
          />
          {selectedAlerts && (
            <Button
              loading={loadingData}
              color="blue"
              icon={'envelope open'}
              labelPosition="right"
              onClick={() => handleFlagMulti(selectedAlerts, false)}
              floated="right"
              content="Toggle selected alerts"
            />
          )}
          <div style={{ height: '35px' }} />
          {!dimmerProcess && (
            <Table celled selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Patient</Table.HeaderCell>
                  <Table.HeaderCell>Alert</Table.HeaderCell>
                  <Table.HeaderCell>Author</Table.HeaderCell>
                  <Table.HeaderCell>Last Updated</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {alertsData.map((flag) => {
                  try {
                    return (
                      <Table.Row key={flag.id} active={flag.status === 'inactive'}>
                        <Table.Cell>
                          <Header as="h4" image>
                            <Image src="../../../../images/daniel.jpg" rounded size="mini" />

                            <Header.Content style={{ cursor: 'pointer' }}>
                              <Label
                                size="large"
                                style={{
                                  backgroundColor: 'white',
                                  textDecoration: 'underline',
                                  textDecorationColor: 'blue',
                                }}
                                onClick={() => openPatient(flag)}
                              >
                                {' '}
                                {(() => {
                                  // Hoisted so the presence test narrows the value
                                  // that is then read; the original tested the same
                                  // subscript twice plus `flag.patient`, which
                                  // narrows nothing. The ternary is kept rather
                                  // than collapsed to `?? flag.patient`: a member
                                  // present but unnamed renders `undefined` today,
                                  // and `??` would silently substitute the id.
                                  const alertPatient = patients?.[mapKey(flag.patient)];
                                  return alertPatient && flag.patient
                                    ? alertPatient.name
                                    : flag.patient;
                                })()}{' '}
                              </Label>
                            </Header.Content>
                          </Header>
                        </Table.Cell>
                        <Table.Cell>{flag.text}</Table.Cell>
                        <Table.Cell>{flag.author}</Table.Cell>
                        <Table.Cell>
                          {flag.period &&
                            moment
                              .utc(flag.period, 'YYYY-MM-DDTHH:mm:ss')
                              .local()
                              .format('MMMM Do, YYYY hh:mm a')}
                          <span className="cb_floatright">
                            <Checkbox
                              onClick={() => handleSelectFlag(flag)}
                              defaultchecked={
                                selectedAlerts.length > 0 && selectedAlerts.indexOf(flag) !== -1
                              }
                            />
                            {'\t'}
                            {
                              <Checkbox
                                onChange={() => changeStatus(flag)}
                                checked={flag.status === 'active' ? true : false}
                                toggle
                              />
                            }
                          </span>
                        </Table.Cell>
                      </Table.Row>
                    );
                  } catch {
                    // try end
                  }
                })}
              </Table.Body>
            </Table>
          )}
        </>
      )}
      {alertsData && !alertsData.length && <Message warning header="No Alerts Found!" />}
      {!alertsData && alertsError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(alertsError as { message?: string }).message}</p>
        </Message>
      )}
    </>
  );
});

export default Alerts;
